import axios from 'axios';
import { call, put, take, takeEvery } from 'redux-saga/effects';
import { api } from 'services/api';
import { PayloadAction } from '@reduxjs/toolkit';
import { notificationContainer } from 'utils/notificationContainer';
import fileDownload from 'js-file-download';
import {
	TCredentialsResetRequest,
	TDeleteUserRequest,
	TEmAssignmentResponse,
	TFetchNotificationSubscriptionsRequest,
	TFetchNotificationSubscriptionsResponsePayload,
	TFetchUsersResponsePayload,
	TGetContractDocumentsResponsePayload,
	TUpdateSettingsRequest,
	TUpdateNotificationSubscriptionRequest,
	TUpdateUserRequest,
	T2FAStatusResponsePayload,
	T2FAEnableRequest,
	T2FADisableRequest,
	TSpecialSchemesResponsePayload,
	TIntegrationsResponsePayload,
	TNewWixIntegrationRequest,
	TNewSquarespaceIntegrationRequest,
	TNewStripeIntegrationRequest,
	TGetHmrcOauthUrlResponsePayload,
	TSetHmrcOauthRequest,
	TUploadedSalesReportsRequest,
	TUploadedSalesReportsResponsePayload,
	TUploadSalesReportRequest,
	TUploadSalesReportResponsePayload,
	TDownloadSalesReportRequest,
	TDownloadSalesReportResponsePayload,
	TCheckHmrcAvailabilityResponsePayload,
	TGetContractDocumentsRequest,
	TDownloadContractDocumentRequest,
	TDownloadContractDocumentResponsePayload,
	TCreateDashboardUserRequest,
	TCreateDashboardUserResponsePayload,
	TSupportedCountryListResponsePayload,
	TAllCountriesResponsePayload,
} from './types';
import {
	deleteUserRequest,
	fetchContractDocumentRequest,
	fetchContractDocumentSuccess,
	getContractDocumentsRequest,
	getContractDocumentsSuccess,
	getEmAssignmentRequest,
	getEmAssignmentSuccess,
	getNotificationSettingsRequest,
	getNotificationSettingsSuccess,
	getNotificationSubscriptionsRequest,
	getNotificationSubscriptionsSuccess,
	resetCredentials,
	resetCredentialsSuccess,
	updateSettingsRequest,
	updateNotificationSubscriptionRequest,
	updateUserRequest,
	confirmCurrentPasswordError,
	get2FAStatusRequest,
	get2FAStatusSuccess,
	enable2FARequest,
	enable2FASuccess,
	enable2FAError,
	disable2FARequest,
	disable2FASuccess,
	disable2FAError,
	getSpecialSchemesRequest,
	getSpecialSchemesSuccess,
	getIntegrationsRequest,
	getIntegrationsSuccess,
	newWixIntegrationRequest,
	newWixIntegrationSuccess,
	newWixIntegrationSubmitError,
	newSquarespaceIntegrationRequest,
	newSquarespaceIntegrationSuccess,
	newSquarespaceIntegrationSubmitError,
	newStripeIntegrationRequest,
	newStripeIntegrationSuccess,
	newStripeIntegrationSubmitError,
	getHmrcOauthUrlRequest,
	getHmrcOauthUrlSuccess,
	setHmrcOauthError,
	setHmrcOauthRequest,
	deleteHmrcOauthRequest,
	getUploadedSalesReportsRequest,
	getUploadedSalesReportsSuccess,
	uploadSalesReportRequest,
	uploadSalesReportSuccess,
	downloadSalesReportRequest,
	downloadSalesReportSuccess,
	getHmrcAvailabilityRequest,
	getHmrcAvailabilitySuccess,
	createDashboardUserRequest,
	createDashboardUserSuccess,
	createDashboardUserError,
	needConfirmAccessCreateDashboardUser,
	getSupportedCountryListRequest,
	getSupportedCountryListSuccess,
	getAllCountriesRequest,
	getAllCountriesSuccess,
} from './reducer';
import { refreshTokenByTimeOutRequest, refreshTokenByTimeOutSuccess } from '../auth/reducer';

function* getEmAssignmentRequestWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TEmAssignmentResponse = yield call(api.user.getEmAssignment);
		yield put(getEmAssignmentSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			// notificationContainer(String(error), 'error');
		}
		// yield put(settingsInitState());
	} finally {
		// yield put(hideLoading());
	}
}

function* updateSettingsRequestWorker(action: PayloadAction<TUpdateSettingsRequest>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.user.updateSettings, payload);
		notificationContainer('Settings successfully edited', 'success');

		yield put(getEmAssignmentRequest());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
	}
}

function* getContractDocumentsRequestWorker(action: PayloadAction<TGetContractDocumentsRequest>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TGetContractDocumentsResponsePayload = yield call(
			api.user.getContractDocuments,
			payload,
		);
		yield put(getContractDocumentsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
		// yield put(settingsInitState());
	}
}

function* fetchContractDocumentRequestWorker(
	action: PayloadAction<TDownloadContractDocumentRequest>,
) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TDownloadContractDocumentResponsePayload = yield call(
			api.user.downloadContractDocument,
			payload,
		);
		// const { data, headers } = response as any;
		// const blobStore = new Blob([data as BlobPart]);
		// // console.log('response', response);
		// // console.log('data', data);
		// // console.log('headers', headers);
		// // console.log('headers[x-suggested-filename]', headers['x-suggested-filename' as any]);
		// if (blobStore) {
		// 	const url = window.URL.createObjectURL(blobStore);
		// 	const a = document.createElement('a');
		// 	document.body.appendChild(a);
		// 	a.href = url;
		// 	const name = headers['x-suggested-filename'];
		// 	a.download = `${String(name)}`;
		// 	a.click();
		// 	window.URL.revokeObjectURL(url);
		// }

		// yield put(fetchContractDocumentSuccess(response));
		yield put(fetchContractDocumentSuccess(null));
		if (response) {
			fileDownload(response, payload.fileName);
		}
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
		// yield put(settingsInitState());
	}
}

function* getNotificationSettingsRequestWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TFetchUsersResponsePayload = yield call(api.user.fetchUsers);
		yield put(getNotificationSettingsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
		// yield put(settingsInitState());
	}
}

function* updateUserRequestWorker(action: PayloadAction<TUpdateUserRequest>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.user.updateUser, payload);
		notificationContainer('User successfully edited', 'success');

		yield put(getNotificationSettingsRequest());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
	}
}

function* deleteUserRequestWorker(action: PayloadAction<TDeleteUserRequest>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.user.deleteUser, payload);
		notificationContainer('User successfully deleted', 'success');

		yield put(getNotificationSettingsRequest());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
	}
}

function* createDashboardUserRequestWorker(action: PayloadAction<TCreateDashboardUserRequest>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TCreateDashboardUserResponsePayload = yield call(
			api.user.createDashboardUser,
			payload,
		);
		yield put(createDashboardUserSuccess(response));
		if (response?.message?.includes('exists already')) {
			yield put(needConfirmAccessCreateDashboardUser(true));
			return;
		}

		notificationContainer(response?.message ?? 'Dashboard user successfully created', 'success');
		yield put(getNotificationSettingsRequest());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const message = String(error.response?.data?.message ?? error ?? '');
			notificationContainer(message, 'error');
			yield put(createDashboardUserError(message));
			return;
		}
		const message = String(error ?? 'Unknown error');
		notificationContainer(message, 'error');
		yield put(createDashboardUserError(message));
	}
}

function* updateNotificationSubscriptionRequestWorker(
	action: PayloadAction<TUpdateNotificationSubscriptionRequest>,
) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.user.updateNotificationSubscription, payload);
		notificationContainer('Notification subscription successfully updated', 'success');

		// yield put(getNotificationSettingsRequest());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
	}
}

function* getNotificationSubscriptionsRequestWorker(
	action: PayloadAction<TFetchNotificationSubscriptionsRequest>,
) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TFetchNotificationSubscriptionsResponsePayload = yield call(
			api.user.fetchNotificationSubscriptions,
			payload,
		);
		yield put(getNotificationSubscriptionsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
		// yield put(settingsInitState());
	}
}

function* resetCredentialsWorker(action: PayloadAction<TCredentialsResetRequest>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.user.resetCredentials, payload);
		notificationContainer('Credentials successfully reset', 'success');
		yield put(resetCredentialsSuccess());

		// yield put(getNotificationSettingsRequest());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(error.response?.data?.message ?? error ?? '', 'error');
			yield put(confirmCurrentPasswordError(error.response?.data?.message ?? error ?? ''));
		}
		// yield put(settingsInitState());
	}
}

function* get2FAStatusRequestWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: T2FAStatusResponsePayload = yield call(api.user.get2FAStatus);
		yield put(get2FAStatusSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			// notificationContainer(String(error), 'error');
		}
		// yield put(settingsInitState());
	} finally {
		// yield put(hideLoading());
	}
}

function* enable2FAWorker(action: PayloadAction<T2FAEnableRequest>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.user.enable2FA, payload);
		yield put(enable2FASuccess());
		// yield put(get2FAStatus());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(error?.response?.data?.message ?? error ?? '', 'error');
			yield put(enable2FAError(error?.response?.data?.message ?? error ?? ''));
		}
	}
}

function* disable2FAWorker(action: PayloadAction<T2FADisableRequest>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.user.disable2FA, payload);
		yield put(disable2FASuccess());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(error?.response?.data?.message ?? error ?? '', 'error');
			yield put(disable2FAError(error?.response?.data?.message ?? error ?? ''));
		}
	}
}

function* getSpecialSchemesRequestWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TSpecialSchemesResponsePayload = yield call(api.user.getSpecialSchemes);
		yield put(getSpecialSchemesSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
	}
}

function* getIntegrationsRequestWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TIntegrationsResponsePayload = yield call(api.user.getIntegrations);
		yield put(getIntegrationsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
	}
}

function* newWixIntegrationRequestWorker(action: PayloadAction<TNewWixIntegrationRequest>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.user.newWixIntegration, payload);
		notificationContainer('New Wix integration successfully added', 'success');
		yield put(newWixIntegrationSuccess());
		yield put(getIntegrationsRequest());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(
					error.response?.data.message ?? 'Something went wrong, Please check value or try later',
				),
				'error',
			);
			yield put(
				newWixIntegrationSubmitError(
					String(
						error.response?.data.message ?? 'Something went wrong, Please check value or try later',
					),
				),
			);
		} else {
			notificationContainer('Something went wrong, Please check value or try later', 'error');
			yield put(
				newWixIntegrationSubmitError('Something went wrong, Please check value or try later'),
			);
		}
	}
}

export function* newSquarespaceIntegrationRequestWorker(
	action: PayloadAction<TNewSquarespaceIntegrationRequest>,
) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.user.newSquarespaceIntegration, payload);
		notificationContainer('New Squarespace integration successfully added', 'success');
		yield put(newSquarespaceIntegrationSuccess());
		yield put(getIntegrationsRequest());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(
					error.response?.data.message ?? 'Something went wrong, Please check value or try later',
				),
				'error',
			);
			yield put(
				newSquarespaceIntegrationSubmitError(
					String(
						error.response?.data.message ?? 'Something went wrong, Please check value or try later',
					),
				),
			);
		} else {
			notificationContainer('Something went wrong, Please check value or try later', 'error');
			yield put(
				newSquarespaceIntegrationSubmitError(
					'Something went wrong, Please check value or try later',
				),
			);
		}
	}
}

export function* newStripeIntegrationRequestWorker(
	action: PayloadAction<TNewStripeIntegrationRequest>,
) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.user.newStripeIntegration, payload);
		notificationContainer('New Stripe integration successfully added', 'success');
		yield put(newStripeIntegrationSuccess());
		yield put(getIntegrationsRequest());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(
					error.response?.data.message ?? 'Something went wrong, Please check value or try later',
				),
				'error',
			);
			yield put(
				newStripeIntegrationSubmitError(
					String(
						error.response?.data.message ?? 'Something went wrong, Please check value or try later',
					),
				),
			);
		} else {
			notificationContainer('Something went wrong, Please check value or try later', 'error');
			yield put(
				newStripeIntegrationSubmitError('Something went wrong, Please check value or try later'),
			);
		}
	}
}

function* getHmrcOauthUrlRequestWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TGetHmrcOauthUrlResponsePayload = yield call(api.user.getHmrcOauthUrl);
		yield put(getHmrcOauthUrlSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data.message ?? error ?? Error), 'error');
		}
	}
}

export function* setHmrcOauthRequestWorker(action: PayloadAction<TSetHmrcOauthRequest>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.user.setHmrcOauth, payload);
		// notificationContainer('HMRC OAuth successfully added', 'success');
		yield put(getIntegrationsRequest());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			// notificationContainer(String(error.response?.data.message ?? error ?? Error), 'error');
			yield put(setHmrcOauthError(error.response?.data.message ?? error.message ?? 'Error'));
		}
	}
}

export function* deleteHmrcOauthRequestWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.user.deleteHmrcOauth);
		notificationContainer('HMRC OAuth successfully deleted', 'success');
		yield put(getIntegrationsRequest());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data.message ?? error ?? Error), 'error');
		}
	}
}

function* getHmrcAvailabilityRequestWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TCheckHmrcAvailabilityResponsePayload = yield call(
			api.user.checkHmrcAvailability,
		);
		yield put(getHmrcAvailabilitySuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			// notificationContainer(String(error.response?.data.message \?? error \?? Error), 'error');
		}
	}
}

export function* getUploadedSalesReportsRequestWorker(
	action: PayloadAction<TUploadedSalesReportsRequest>,
) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TUploadedSalesReportsResponsePayload = yield call(
			api.user.getUploadedSalesReports,
			payload,
		);
		yield put(getUploadedSalesReportsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data.message ?? error ?? Error), 'error');
		}
	}
}

export function* uploadSalesReportRequestWorker(action: PayloadAction<TUploadSalesReportRequest>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TUploadSalesReportResponsePayload = yield call(
			api.user.uploadSalesReport,
			payload.formData,
		);
		yield put(uploadSalesReportSuccess(response));
		yield put(getUploadedSalesReportsRequest(payload.reloadBody));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data.message ?? error ?? Error), 'error');
		}
	}
}

export function* downloadSalesReportRequestWorker(
	action: PayloadAction<TDownloadSalesReportRequest>,
) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TDownloadSalesReportResponsePayload = yield call(
			api.user.downloadSalesReport,
			payload,
		);
		yield put(downloadSalesReportSuccess(response));
		if (response) {
			fileDownload(response, payload.fileName);
		}
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data.message ?? error ?? Error), 'error');
		}
	}
}

function* getSupportedCountryListRequestWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TSupportedCountryListResponsePayload = yield call(
			api.user.getSupportedCountryList,
		);
		yield put(getSupportedCountryListSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			// notificationContainer(String(error.response?.data?.message ?? error ?? Error), 'error');
		}
	}
}

function* getAllCountriesRequestWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TAllCountriesResponsePayload = yield call(api.user.getAllCountries);
		yield put(getAllCountriesSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? error ?? Error), 'error');
		}
	}
}

export function* settingsSaga() {
	yield takeEvery(getEmAssignmentRequest.type, getEmAssignmentRequestWorker);
	yield takeEvery(updateSettingsRequest.type, updateSettingsRequestWorker);
	yield takeEvery(getContractDocumentsRequest.type, getContractDocumentsRequestWorker);
	yield takeEvery(fetchContractDocumentRequest.type, fetchContractDocumentRequestWorker);
	yield takeEvery(getNotificationSettingsRequest.type, getNotificationSettingsRequestWorker);
	yield takeEvery(updateUserRequest.type, updateUserRequestWorker);
	yield takeEvery(deleteUserRequest.type, deleteUserRequestWorker);
	yield takeEvery(createDashboardUserRequest.type, createDashboardUserRequestWorker);
	yield takeEvery(
		updateNotificationSubscriptionRequest.type,
		updateNotificationSubscriptionRequestWorker,
	);
	yield takeEvery(
		getNotificationSubscriptionsRequest.type,
		getNotificationSubscriptionsRequestWorker,
	);
	yield takeEvery(resetCredentials.type, resetCredentialsWorker);
	yield takeEvery(get2FAStatusRequest.type, get2FAStatusRequestWorker);
	yield takeEvery(enable2FARequest.type, enable2FAWorker);
	yield takeEvery(disable2FARequest.type, disable2FAWorker);
	yield takeEvery(getSpecialSchemesRequest.type, getSpecialSchemesRequestWorker);
	yield takeEvery(getIntegrationsRequest.type, getIntegrationsRequestWorker);
	yield takeEvery(newWixIntegrationRequest.type, newWixIntegrationRequestWorker);
	yield takeEvery(newSquarespaceIntegrationRequest.type, newSquarespaceIntegrationRequestWorker);
	yield takeEvery(newStripeIntegrationRequest.type, newStripeIntegrationRequestWorker);
	yield takeEvery(getHmrcOauthUrlRequest.type, getHmrcOauthUrlRequestWorker);
	yield takeEvery(setHmrcOauthRequest.type, setHmrcOauthRequestWorker);
	yield takeEvery(deleteHmrcOauthRequest.type, deleteHmrcOauthRequestWorker);
	yield takeEvery(getUploadedSalesReportsRequest.type, getUploadedSalesReportsRequestWorker);
	yield takeEvery(uploadSalesReportRequest.type, uploadSalesReportRequestWorker);
	yield takeEvery(downloadSalesReportRequest.type, downloadSalesReportRequestWorker);
	yield takeEvery(getHmrcAvailabilityRequest.type, getHmrcAvailabilityRequestWorker);
	yield takeEvery(getSupportedCountryListRequest.type, getSupportedCountryListRequestWorker);
	yield takeEvery(getAllCountriesRequest.type, getAllCountriesRequestWorker);
}
